import {TemplateNode} from "#template/template-node/template-node"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {InputNodeParameters} from "#template/fundamental/input/types"
import {fundamentalLikeMarker, type FundamentalLikeNode} from "#template/role-markers"
import type {EvaluatedValueOf, InfoClassCtor} from "#template/fundamental/factory"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {OnCompileContext, VisitorNodeResult, TemplateNodeContext} from "#template/template-node/types"
import type {Evaluator} from "@cm/graph-v3"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {evaluateMetaData} from "#template/nodes/meta-data"
import {resolveTemplateInput, getAffectedTemplateNodeParts} from "#template/fundamental/helpers"
import {OutputNode} from "#template/fundamental/output/output-node"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

const objectPathPartArrayDesc = TD.Array(TD.ObjectPathPart)
const stringArrayDesc = TD.Array(TD.Primitive<string>())

/*Input logic:
- Every input node of a template creates an input descriptor to publish which ids can be set in the parameters.
- When a template instance is compiled it takes the Parameters of template instance, evaluates them using evaluateTemplateInputs and passes this to CompileTemplate.
- In CompileTemplate a local evaluator using the evaluated inputs will be created.
- When an input is evaluated, it is indexing the value from the evaluated inputs from the evaluator using its id.
- If the input is not set, the default value is evaluated and returned instead.*/

export abstract class InputNode<out ItemValue = unknown>
    extends TemplateNode<InputNodeParameters<ItemValue>>
    implements FundamentalLikeNode<EvaluatedValueOf<ItemValue> | null>
{
    get [fundamentalLikeMarker]() {
        return true as const
    }

    abstract get ancestry(): readonly string[]
    abstract get InfoClass(): InfoClassCtor

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<EvaluatedValueOf<ItemValue> | null> {
        const {templateScope} = evaluator
        return scope.unresolvedToNull(templateScope.resolve<EvaluatedValueOf<ItemValue> | null>(`templateInputs-${evaluator.getLocalId(this)}`))
    }

    evaluateImpl(
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): BuilderInlet<{value: EvaluatedValueOf<ItemValue> | null; origin: TemplateNode | undefined}> {
        const templateInput = this.getTemplateInput(scope, evaluator)
        if (templateInput === undefined)
            return scope.struct<{value: EvaluatedValueOf<ItemValue> | null; origin: TemplateNode | undefined}>(`${this.definition.nodeClass}Data`, {
                value: evaluator.evaluate((this.parameters.default ?? null) as ItemValue | null) as BuilderInlet<EvaluatedValueOf<ItemValue> | null>,
                origin: this.parameters.default instanceof TemplateNode ? this.parameters.default : undefined,
            })
        else return templateInput
    }

    getTemplateInput(
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): BuilderInlet<{value: EvaluatedValueOf<ItemValue>; origin: TemplateNode | undefined}> | undefined {
        return resolveTemplateInput<EvaluatedValueOf<ItemValue>>(scope, evaluator, this.parameters.id, this.ancestry[0])
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<InputNodeParameters<ItemValue>> {
        const {id, name: nodeName, metaData} = this.parameters
        const {evaluator, currentTemplate, path} = context
        const {templateScope, activeNodeSet} = evaluator
        const scope = evaluator.getScope(this)
        const {descriptorList} = currentTemplate

        const data = this.evaluateImpl(scope, evaluator)
        const evaluatedMetaData = evaluateMetaData(scope, evaluator, metaData, id)

        const {parts, affectedOutputIds: affectedOutputIdsRaw} = getAffectedTemplateNodeParts(this, activeNodeSet, (node) => node instanceof OutputNode)
        const affectedObjectPathParts = scope.value(
            parts.map(({templateNode: n, part}) => ({
                path: [...path, n],
                part,
            })),
            objectPathPartArrayDesc,
            "affectedObjectPathParts",
        )

        const affectedOutputIds = scope.value(affectedOutputIdsRaw, stringArrayDesc, "affectedOutputIds")

        const InfoClass = this.InfoClass
        const ftName = this.ancestry[0]

        descriptorList.push(
            scope.pureLambda(
                scope.tuple(id, nodeName, data, evaluatedMetaData, affectedObjectPathParts, affectedOutputIds, InfoClass),
                ([id, name, {value, origin}, metaData, affectedObjectPathParts, affectedOutputIds, InfoClass]) =>
                    new InfoClass({
                        id,
                        name,
                        value,
                        type: "input" as const,
                        origin,
                        metaData,
                        affectedObjectPathParts,
                        affectedOutputIds,
                    }),
                `${ftName}Info`,
            ),
        )

        templateScope.alias(scope.get(data, "value"), `templateInputs-${evaluator.getLocalId(this)}`)

        return visitNone(this.parameters)
    }
}
